import React, { Fragment, useState, useEffect, useMemo } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import StickyBox from 'react-sticky-box'
import Breadcrumb from '../Breadcrumb'
import Sidebar from '../Sidebar'
import { Alerte_Error } from '../../../ui/AlertesUi'
import { BtnLoading } from '../../../ui/BtnUi'
import { ToastError, ToastConfirm } from '../../../ui/AlertesUi'
import { Api } from '../../auth/Configs'
import { USERS_SESSION, GET_USERS_ACCOUNT, UPDATE_USER_ACCOUNT, PLAN_FOLDERS, PLAN_META_LIST } from '../../auth/urlParameters'
import useAxiosToGet from '../../fetchDatasAxios/FetchGet'
import { AxiosGetDatas } from '../../fetchDatasAxios/FetchGetDatas'
import axios from 'axios'
import ServiceMultiSelect from './ServiceMultiSelect'
import './UpdateUserAccount.css'

const roleOptions = [
  {
    key: 'archiviste',
    label: 'Archiviste',
    role: 'ROLE_EDITED',
    details: ['Gère plans/dossiers', 'Valide conservation', 'Supervise indexation'],
  },
  {
    key: 'assistant',
    label: 'Assistant',
    role: 'ROLE_ASSIST',
    details: ['Dépose et met à jour dans ses dossiers autorisés'],
  },
  {
    key: 'lecteur',
    label: 'Lecteur',
    role: 'ROLE_USER',
    details: ['Lecture seule', 'Sans export si non autorisé'],
  },
  {
    key: 'admin',
    label: 'Admin',
    role: 'ROLE_ADMIN',
    details: ['Gestion comptes', 'Rôles', 'Paramètres globaux', 'Audit'],
  },
]

const defaultAccessFilters = {
  planIds: [],
  folderIds: [],
  maxConfidentialite: '',
  allowExport: true,
}

const flattenServices = (nodes, depth = 0) =>
  (nodes || []).reduce((acc, node) => {
    acc.push({
      id: node.id,
      label: `${'-- '.repeat(depth)}${node.code} - ${node.title}`,
    })
    if (node.children && node.children.length > 0) {
      acc.push(...flattenServices(node.children, depth + 1))
    }
    return acc
  }, [])

const flattenFolders = (nodes, depth = 0) =>
  (nodes || []).reduce((acc, node) => {
    acc.push({
      id: node.id,
      planId: node.planId || null,
      label: `${'-- '.repeat(depth)}${node.reference} - ${node.title}`,
    })
    if (node.children && node.children.length > 0) {
      acc.push(...flattenFolders(node.children, depth + 1))
    }
    return acc
  }, [])

const roleKeyFromRoles = (roles = []) => {
  if (roles.includes('ROLE_ADMIN')) return 'admin'
  if (roles.includes('ROLE_EDITED')) return 'archiviste'
  if (roles.includes('ROLE_ASSIST')) return 'assistant'
  return 'lecteur'
}

const generatePassword = (length = 12) => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789!@#$%';
  const values = new Uint32Array(length);
  const cryptoObj = window.crypto || window.msCrypto;
  if (cryptoObj && cryptoObj.getRandomValues) {
    cryptoObj.getRandomValues(values);
    return Array.from(values, (value) => chars[value % chars.length]).join('');
  }
  return Array.from({ length }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
}

const AccessMultiSelect = ({ label, options, selected, onChange, disabled = false, emptyLabel, help }) => {
  const [query, setQuery] = useState('')
  const selectedIds = selected.map(Number)
  const selectedOptions = options.filter((option) => selectedIds.includes(Number(option.id)))
  const visibleOptions = options.filter((option) =>
    option.label.toLocaleLowerCase('fr').includes(query.toLocaleLowerCase('fr'))
  )
  const toggle = (id) => {
    const numericId = Number(id)
    onChange(selectedIds.includes(numericId)
      ? selectedIds.filter((value) => value !== numericId)
      : [...selectedIds, numericId])
  }

  return (
    <div className={`user-edit-access-select${disabled ? ' is-disabled' : ''}`}>
      <span className="user-edit-label">{label}</span>
      <div className={`user-edit-selected-items${selectedOptions.length > 0 ? ' has-selection' : ''}`} aria-live="polite">
        {selectedOptions.length === 0 ? <span className="user-edit-all-access">{emptyLabel}</span> :
          selectedOptions.map((option) => (
            <span className="user-edit-chip" key={option.id}>
              {option.label}
              <button type="button" aria-label={`Retirer ${option.label}`} onClick={() => toggle(option.id)} disabled={disabled}>×</button>
            </span>
          ))}
      </div>
      {disabled ? <div className="user-edit-choice-disabled">Aucune restriction de dossier</div> :
        <details className="user-edit-choice-list">
          <summary>Choisir {label.toLocaleLowerCase('fr')}</summary>
          <div className="user-edit-choice-panel">
            <input
              type="search"
              className="form-control"
              placeholder="Rechercher…"
              aria-label={`Rechercher dans ${label.toLocaleLowerCase('fr')}`}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
            <div className="user-edit-choice-options">
              {visibleOptions.length === 0 ? <span className="user-edit-no-choice">Aucun résultat</span> :
                visibleOptions.map((option) => (
                  <label className="user-edit-choice" key={option.id}>
                    <input
                      type="checkbox"
                      checked={selectedIds.includes(Number(option.id))}
                      onChange={() => toggle(option.id)}
                    />
                    <span>{option.label}</span>
                  </label>
                ))}
            </div>
          </div>
        </details>}
      <small className="user-edit-help">{help}</small>
    </div>
  )
}

const UpdateUserAccount = () => {
  const navigate = useNavigate()
  const userAccount = {
    username: '',
    email: '',
    roles: null,
    etat: ''
  }
  const state = useSelector(state => state.sessionAuth)
  const[nullAuth, setNullAuth] = useState(false)
  const[user, setUser] = useState(userAccount)
  const[subLoading, setSubLoading]= useState(false)
  const[services, setServices] = useState([])
  const[loadingServices, setLoadingServices] = useState(false)
  const[serviceIds, setServiceIds] = useState([])
  const[plans, setPlans] = useState([])
  const[folders, setFolders] = useState([])
  const[accessFilters, setAccessFilters] = useState(defaultAccessFilters)
  const[roleKey, setRoleKey] = useState('assistant')
  const[resetPassword, setResetPassword] = useState('')
  const[resetLoading, setResetLoading] = useState(false)
  const[resetDone, setResetDone] = useState(false)
  const[showResetPassword, setShowResetPassword] = useState(false)
  
  const params = useParams()
 
  const {isAppLoading, fetchDatas} = useAxiosToGet(USERS_SESSION(), state.token);

  const authHeaders = useMemo(
    () => ({
      headers: { Authorization: 'Bearer ' + state.token },
    }),
    [state.token]
  )

  const selectedRole = useMemo(
    () => roleOptions.find((option) => option.key === roleKey) || roleOptions[1],
    [roleKey]
  )
  //fonction get update compte
  const GetUpdateUser = () =>{
    const config = AxiosGetDatas(GET_USERS_ACCOUNT(params.user), state.token);
    axios(config).then(response =>{
      let account = response.data
      setUser({
        ...userAccount,
        ...account
      })
      const roles = Array.isArray(account.roles) ? account.roles : []
      setRoleKey(roleKeyFromRoles(roles))
      const profile = account.profile || {}
      const filters = profile.accessFilters || {}
      setAccessFilters({
        planIds: Array.isArray(filters.planIds) ? filters.planIds : [],
        folderIds: Array.isArray(filters.folderIds) ? filters.folderIds : [],
        maxConfidentialite: filters.maxConfidentialite || '',
        allowExport: filters.allowExport !== false,
      })
      const assignedServices = Array.isArray(profile.serviceUnits) ? profile.serviceUnits : []
      const ids = assignedServices.map((service) => Number(service?.id ?? service)).filter((id) => Number.isInteger(id) && id > 0)
      if (profile.serviceUnit) {
        const primaryId = Number(profile.serviceUnit?.id ?? profile.serviceUnit)
        if (Number.isInteger(primaryId) && primaryId > 0) ids.unshift(primaryId)
      }
      setServiceIds([...new Set(ids)])
    }).catch(error =>{console.log(error)})
  }
    useEffect(()=>{
        
        if(fetchDatas !== null){
         
          if(params.sessionId !== fetchDatas.data.useruuid || parseInt(params.id) !== fetchDatas.data.id){
            setNullAuth(true)
          }
          GetUpdateUser()
           
        }
    // eslint-disable-next-line 
    },[fetchDatas])

    useEffect(() => {
      setLoadingServices(true)
      axios
        .get(`${Api}/services`, authHeaders)
        .then((res) => {
          const flat = flattenServices(res.data?.services || [])
          setServices(flat)
        })
        .catch(() => setServices([]))
        .finally(() => setLoadingServices(false))

      axios
        .get(PLAN_META_LIST(), authHeaders)
        .then((res) => {
          setPlans(res.data?.plans || [])
        })
        .catch(() => setPlans([]))

      axios
        .get(PLAN_FOLDERS(), authHeaders)
        .then((res) => {
          const flat = flattenFolders(res.data?.folders || [])
          setFolders(flat)
        })
        .catch(() => setFolders([]))
    }, [authHeaders])

    const filteredFolders = useMemo(() => {
      if (!accessFilters.planIds || accessFilters.planIds.length === 0) {
        return folders
      }
      const allowedPlans = accessFilters.planIds.map(Number)
      return folders.filter((folder) => folder.planId && allowedPlans.includes(Number(folder.planId)))
    }, [accessFilters.planIds, folders])

    useEffect(() => {
      if (accessFilters.planIds.length === 0 && accessFilters.folderIds.length > 0) {
        setAccessFilters((prev) => ({ ...prev, folderIds: [] }))
      }
    }, [accessFilters.planIds, accessFilters.folderIds])

    useEffect(() => {
      if (!accessFilters.folderIds || accessFilters.folderIds.length === 0) {
        return
      }
      if (!accessFilters.planIds || accessFilters.planIds.length === 0) {
        return
      }
      const allowedIds = new Set(filteredFolders.map((folder) => folder.id))
      const filteredSelection = accessFilters.folderIds.filter((id) => allowedIds.has(Number(id)))
      if (filteredSelection.length !== accessFilters.folderIds.length) {
        setAccessFilters((prev) => ({ ...prev, folderIds: filteredSelection }))
      }
    }, [accessFilters.folderIds, accessFilters.planIds, filteredFolders])

    const handleRoleSelect = (key) => {
      setRoleKey(key)
      const option = roleOptions.find((role) => role.key === key)
      if (option) {
        setUser((prev) => ({ ...prev, roles: [option.role] }))
      }
      if (key === 'lecteur') {
        setAccessFilters((prev) => ({ ...prev, allowExport: false }))
      }
    }

    //onchange
    const UpdateChange = e =>{
      setUser({
        ...user,
        [e.target.name] : e.target.value
      })
    }
   // console.log(user)
    //submitForm

    const submitForm = async (e)=>{
      e.preventDefault()
      setSubLoading(true)
      const resolvedRoles = Array.isArray(user.roles) && user.roles.length > 0
        ? user.roles
        : [selectedRole.role]
      const payload = {
        username: user.username,
        email: user.email,
        roles: resolvedRoles,
        etat: user.etat,
        accessFilters: {
          planIds: accessFilters.planIds || [],
          folderIds: accessFilters.planIds?.length ? accessFilters.folderIds || [] : [],
          maxConfidentialite: accessFilters.maxConfidentialite || null,
          allowExport: accessFilters.allowExport !== false,
        },
        serviceIds,
      }
      try {
        await axios.put(UPDATE_USER_ACCOUNT(user.id, user.useruuid), payload, authHeaders)
        ToastConfirm('Compte modifié avec succès')
      } catch (error) {
        ToastError(error.response?.data?.message || 'Échec de la modification du compte')
      } finally {
        setSubLoading(false)
      }
    }

    const handleGenerateResetPassword = () => {
      const password = generatePassword()
      setResetPassword(password)
      setResetDone(false)
    }

    const handleCopyResetPassword = () => {
      if (!resetPassword) return
      if (navigator?.clipboard?.writeText) {
        navigator.clipboard
          .writeText(resetPassword)
          .then(() => ToastConfirm('Mot de passe copié'))
          .catch(() => ToastError('Impossible de copier le mot de passe'))
      }
    }

    const handleResetPassword = () => {
      if (!resetPassword || resetPassword.length < 8) {
        ToastError('Le mot de passe doit contenir au moins 8 caractères')
        return
      }
      setResetLoading(true)
      axios
        .put(UPDATE_USER_ACCOUNT(user.id, user.useruuid), { password: resetPassword }, authHeaders)
        .then(() => {
          ToastConfirm('Mot de passe réinitialisé')
          setResetDone(true)
        })
        .catch((err) => {
          ToastError(err.response?.data?.message || 'Erreur de réinitialisation du mot de passe')
          setResetDone(false)
        })
        .finally(() => setResetLoading(false))
    }
  return !isAppLoading && (
    nullAuth ?(<div style={{'width':'60%', 'margin':'auto', 'marginTop':'60px', 'marginBottom':'260px'}} > {Alerte_Error('Action non autorisée ! ')} </div>) 
    :(<Fragment>
    <Breadcrumb title="Administration" submenu="Modifier un utilisateur" />

    <div className="content user-edit-page">
    <div className="container-fluid">
        <div className="row">
        <div className="col-md-5 col-lg-4 col-xl-3 theiaStickySidebar">
        {/*Sidebar */}
        <StickyBox offsetTop={20} offsetBottom={20}>
        <Sidebar/>
        </StickyBox>                
        {/*Sidebar */}
        </div>
        {/*application */}
        <div className="col-md-7 col-lg-8 col-xl-9">
        <div className="user-edit-layout">
          <header className="user-edit-header">
            <div>
              <span className="user-edit-eyebrow">ADMINISTRATION · UTILISATEURS</span>
              <h1>Modifier un utilisateur</h1>
              <p><strong>{user.username || 'Compte utilisateur'}</strong> · {selectedRole.label} <span className="user-edit-status">{user.etat || 'Statut inconnu'}</span></p>
            </div>
            <button className="btn btn-outline-primary" type="button" onClick={() => navigate('/apps/in/users-accounts/page/1')}>
              Retour aux utilisateurs
            </button>
          </header>

        <form className="user-edit-form" onSubmit={submitForm}>
          <div className="user-edit-top-grid">
          <div className="card user-edit-card">
            <div className="card-body">
              <h2 className="user-edit-card-title">Informations du compte</h2>
              <p className="user-edit-card-intro">Identité, contact et état du compte.</p>
              <div className="user-edit-fields">
                <div>
                  <div className="form-group">
                    <label htmlFor="user-edit-username">Nom d'utilisateur</label>
                    <input
                      id="user-edit-username"
                      name='username'
                      type="text"
                      className="form-control"
                      value={user.username || ''}
                      onChange={UpdateChange}
                      required
                    />
                  </div>
                </div>
                <div>
                  <div className="form-group">
                    <label htmlFor="user-edit-email">Email du compte (optionnel)</label>
                    <input
                      id="user-edit-email"
                      name='email'
                      type="email"
                      className="form-control"
                      value={user.email || ''}
                      onChange={UpdateChange}
                    />
                  </div>
                </div>
                <div>
                  <div className="form-group">
                    <label htmlFor="user-edit-status">Statut du compte</label>
                    <select id="user-edit-status" name="etat" className="form-control" value={user.etat || ''} onChange={UpdateChange}>
                      {user.etat && !['actif', 'vérrouillé', 'suspendu'].includes(user.etat) && <option value={user.etat}>{user.etat}</option>}
                      <option value="actif">Actif</option>
                      <option value="vérrouillé">Verrouillé</option>
                      <option value="suspendu">Suspendu</option>
                    </select>
                  </div>
                </div>
                <div>
                  <div className="form-group">
                    <ServiceMultiSelect services={services} selected={serviceIds} onChange={setServiceIds} disabled={loadingServices} />
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="card user-edit-card">
            <div className="card-body">
              <h2 className="user-edit-card-title">Rôle & responsabilités</h2>
              <p className="user-edit-card-intro">Sélectionnez le niveau de gestion accordé à l'utilisateur.</p>
              <div className="user-edit-role-grid">
                {roleOptions.map((option) => (
                  <div key={option.key}>
                    <label
                      className={`user-edit-role-option${roleKey === option.key ? ' is-selected' : ''}`}
                    >
                      <div className="user-edit-role-heading">
                        <input
                          type="radio"
                          name="role"
                          value={option.key}
                          checked={roleKey === option.key}
                          onChange={() => handleRoleSelect(option.key)}
                        />
                        <strong>{option.label}</strong>
                      </div>
                      <ul>
                        {option.details.map((detail) => (
                          <li key={detail}>{detail}</li>
                        ))}
                      </ul>
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </div>
          </div>

          <div className="card user-edit-card user-edit-access-card">
            <div className="card-body">
              <h2 className="user-edit-card-title">Accès aux archives</h2>
              <p className="user-edit-card-intro">Définissez le périmètre de consultation et les actions autorisées.</p>
              <div className="user-edit-access-grid">
                <AccessMultiSelect
                  label="Plans autorisés"
                  options={plans.map((plan) => ({ id: plan.id, label: `${plan.code} — ${plan.title}` }))}
                  selected={accessFilters.planIds}
                  onChange={(planIds) => setAccessFilters((prev) => ({ ...prev, planIds }))}
                  emptyLabel="Tous les plans"
                  help="Aucune sélection donne accès à tous les plans."
                />
                <AccessMultiSelect
                  label="Dossiers autorisés"
                  options={filteredFolders}
                  selected={accessFilters.folderIds}
                  onChange={(folderIds) => setAccessFilters((prev) => ({ ...prev, folderIds }))}
                  disabled={accessFilters.planIds.length === 0}
                  emptyLabel={accessFilters.planIds.length === 0 ? 'Tous les dossiers' : 'Tous les dossiers des plans sélectionnés'}
                  help={accessFilters.planIds.length === 0 ? 'Aucune sélection donne accès à tous les dossiers et sous-dossiers, dans le périmètre des autres restrictions du compte.' : `Sans dossier sélectionné, tous les dossiers et sous-dossiers des plans autorisés sont accessibles${roleKey === 'lecteur' || roleKey === 'assistant' ? ', dans le périmètre des services attribués' : ''}.`}
                />
                <div className="form-group">
                  <label htmlFor="user-edit-confidentiality">Confidentialité maximale</label>
                  <select
                    id="user-edit-confidentiality"
                    className="form-control"
                    value={accessFilters.maxConfidentialite}
                    onChange={(e) => setAccessFilters((prev) => ({ ...prev, maxConfidentialite: e.target.value }))}
                  >
                    <option value="">Sans limite</option>
                    <option value="Interne">Interne</option>
                    <option value="Restreint">Restreint</option>
                    <option value="Confidentiel">Confidentiel</option>
                    <option value="Tres Confidentiel">Très confidentiel</option>
                  </select>
                </div>
                <label className="user-edit-export-option">
                  <input
                    type="checkbox"
                    checked={accessFilters.allowExport !== false}
                    onChange={(e) => setAccessFilters((prev) => ({ ...prev, allowExport: e.target.checked }))}
                  />
                  <span><strong>Autoriser le téléchargement</strong><small>Permet d'exporter les archives accessibles.</small></span>
                </label>
              </div>
              <div className="user-edit-access-summary">
                <strong>Résumé des accès</strong>
                <span>{accessFilters.planIds.length || 'Tous les'} plan{accessFilters.planIds.length === 1 ? '' : 's'} · {accessFilters.folderIds.length || 'Tous les'} dossier{accessFilters.folderIds.length === 1 ? '' : 's'} · {accessFilters.maxConfidentialite || 'Sans limite de confidentialité'} · Téléchargement {accessFilters.allowExport ? 'autorisé' : 'désactivé'}</span>
              </div>
            </div>
          </div>

          <div className="user-edit-save-actions">
            <button type="button" className="btn btn-outline-primary" onClick={() => navigate('/apps/in/users-accounts/page/1')}>Annuler</button>
            {!subLoading ? <button type="submit" className="btn btn-primary">Enregistrer</button> : BtnLoading('Modification en cours')}
          </div>
        </form>

          <div className="card user-edit-card user-edit-password-card">
            <div className="card-body">
              <h2 className="user-edit-card-title">Réinitialiser le mot de passe</h2>
              <p className="user-edit-card-intro">Cette action est indépendante des modifications du compte.</p>
              <div className="form-group user-edit-password-field">
                <label htmlFor="user-edit-new-password">Nouveau mot de passe</label>
                <div className="user-edit-password-grid">
                  <div className="user-edit-password-input">
                    <input
                      id="user-edit-new-password"
                      type={showResetPassword ? 'text' : 'password'}
                      className="form-control"
                      value={resetPassword}
                      onChange={(e) => {
                        setResetPassword(e.target.value)
                        setResetDone(false)
                      }}
                    />
                    <button
                      type="button"
                      className="btn btn-outline-secondary user-edit-password-visibility"
                      onClick={() => setShowResetPassword((prev) => !prev)}
                      aria-label={showResetPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                      title={showResetPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                    >
                      <i className={showResetPassword ? 'ri-eye-off-line' : 'ri-eye-line'} aria-hidden="true" />
                    </button>
                  </div>
                  <div className="user-edit-password-actions">
                    <button
                      type="button"
                      className="btn btn-outline-primary"
                      onClick={handleGenerateResetPassword}
                    >
                      Générer
                    </button>
                    <button
                      type="button"
                      className="btn btn-danger"
                      onClick={handleResetPassword}
                      disabled={resetLoading || !resetPassword}
                    >
                      {resetLoading ? 'Réinitialisation...' : 'Réinitialiser'}
                    </button>
                  </div>
                </div>
                <small className="form-text text-muted">Minimum 8 caractères.</small>
              </div>
              {resetDone && (
                <div className="alert alert-success d-flex align-items-center justify-content-between mb-0">
                  <span>Mot de passe réinitialisé. Copiez-le pour l'utilisateur.</span>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-success"
                    onClick={handleCopyResetPassword}
                  >
                    Copier
                  </button>
                </div>
              )}
            </div>
          </div>

        </div></div>
        {/*fin app */}
        </div>
    </div>
</div>
</Fragment>	)
  )
}

export default UpdateUserAccount
